'use client';

import React, { useEffect, useRef, useState } from 'react';
import Icon from '@/components/ui/AppIcon';
import { useLanguage } from '@/contexts/LanguageContext';

interface FinalCTASectionProps {
  onApplyClick: () => void;
}

const FinalCTASection = ({ onApplyClick }: FinalCTASectionProps) => {
  const { t } = useLanguage();
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setIsVisible(true); },
      { threshold: 0.2 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} id="aplicar" className="py-32 bg-white">
      <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
        <div className={`transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          {/* Eyebrow */}
          <div className="inline-flex items-center gap-2 mb-8">
            <div className="w-1.5 h-1.5 rounded-full bg-[#C9A84C]" />
            <span className="text-sm font-body font-medium text-[#C9A84C] tracking-wide uppercase">
              {t('finalCta.eyebrow')}
            </span>
          </div>

          {/* Headline */}
          <h2 className="font-display font-bold text-[#0A1628] leading-tight tracking-tight mb-6"
            style={{ fontSize: 'clamp(2.25rem, 5vw, 4rem)' }}>
            {t('finalCta.headline')}{' '}
            <span className="gradient-text-blue">{t('finalCta.headlineAccent')}</span>
          </h2>

          {/* Subtitle */}
          <p className="font-body text-xl text-[#4A5568] leading-relaxed mb-12 max-w-2xl mx-auto">
            {t('finalCta.subtitle')}
          </p>

          {/* CTA Button */}
          <button
            onClick={onApplyClick}
            className="inline-flex items-center gap-3 bg-[#0A1628] text-white font-display font-semibold text-lg px-10 py-5 rounded-2xl hover:bg-[#1D6FE8] transition-all duration-350 shadow-cta hover:shadow-blue hover:-translate-y-1 animate-pulse-gold"
          >
            {t('finalCta.button')}
            <Icon name="ArrowRightIcon" size={20} />
          </button>

          {/* Note */}
          <p className="mt-6 text-sm font-body text-[#4A5568]/60">
            {t('finalCta.note')}
          </p>

          {/* Trust badges */}
          <div className="mt-16 flex items-center justify-center gap-8 flex-wrap">
            {[
              { icon: 'ShieldCheckIcon', label: 'Verified Program' },
              { icon: 'GlobeAltIcon', label: '30+ Countries' },
              { icon: 'UserGroupIcon', label: '847+ Ambassadors' },
            ].map((badge) => (
              <div key={badge.label} className="flex items-center gap-2 text-[#4A5568]/60">
                <Icon name={badge.icon as any} size={16} />
                <span className="text-sm font-body">{badge.label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default FinalCTASection;
